import { Box, TextInput, SimpleGrid, Text, Divider, Flex, Badge, Button, FileButton, Anchor, Paper } from "@mantine/core";
import "react-international-phone/style.css";
import { PhoneInput } from "react-international-phone";
import { IconFileText, IconUpload, IconTrash } from "@tabler/icons-react";
import { errorNotification } from "@/shared/utils/errorNotification";

interface PersonalDetailsProps {
  formData: {
    userName: string;
    userDesignation: string;
    userPhoneNumber: string;
    userPhoneCountryCode: string;
  };
  updateForm: (field: string, value: any) => void;
  mandatoryData?: {
    id?: string;
    name: string;
    file: File | null;
    url: string;
    existingUrl?: string;
  };
  authorityLetterData?: {
    id?: string;
    name: string;
    file: File | null;
    existingUrl?: string;
  };
  onUpdateMandatory?: (field: string, value: any) => void;
  onUpdateAuthorityLetter?: (field: string, value: any) => void;
  onRemoveMandatoryDoc?: () => void;
  onRemoveAuthorityLetterDoc?: () => void;
  isViewMode?: boolean;
  isEditMode?: boolean;
  companyStatus?: string | null;
}

export default function PersonalDetails({
  formData,
  updateForm,
  mandatoryData,
  authorityLetterData,
  onUpdateMandatory,
  onUpdateAuthorityLetter,
  onRemoveMandatoryDoc,
  onRemoveAuthorityLetterDoc,
  isViewMode = false,
  isEditMode = false,
  companyStatus,
}: PersonalDetailsProps) {
  const showDocs = mandatoryData !== undefined || authorityLetterData !== undefined;
  // Can upload/remove only in create mode OR when company is blocked (ACTION_REQUIRED)
  const canEditMandatory = !isViewMode && (!isEditMode || companyStatus === "ACTION_REQUIRED");

  // If one doc is already uploaded from DB, only show that one
  // Private files may have empty url — use id as the primary indicator of existence
  const hasExistingAuthority = !!(authorityLetterData?.id || authorityLetterData?.existingUrl);
  const hasExistingAffidavit = !!(mandatoryData?.id || mandatoryData?.existingUrl);
  const hasEitherExisting = hasExistingAuthority || hasExistingAffidavit;

  const showAuthorityLetterSection = authorityLetterData !== undefined && (!hasEitherExisting || hasExistingAuthority);
  const showAffidavitSection = mandatoryData !== undefined && (!hasEitherExisting || hasExistingAffidavit);
  const showOrDivider = showAuthorityLetterSection && showAffidavitSection;

  const handleAuthorityLetterUpload = (file: File | null) => {
    if (!file || !onUpdateAuthorityLetter) return;
    if (file.size > 10 * 1024 * 1024) {
      errorNotification({ title: "File too large", message: "Maximum file size is 10MB." });
      return;
    }
    onUpdateAuthorityLetter("file", file);
  };

  const handleAffidavitUpload = (file: File | null) => {
    if (!file || !onUpdateMandatory) return;
    if (file.size > 10 * 1024 * 1024) {
      errorNotification({ title: "File too large", message: "Maximum file size is 10MB." });
      return;
    }
    onUpdateMandatory("file", file);
  };

  const formatDate = (date: any) =>
    new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  return (
    <Box>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="md">
        {/* Name */}
        <TextInput
          label={
            <Text fz="sm" fw={500}>
              Name{" "}
              <Text span c="red" fz="lg">
                *
              </Text>
            </Text>
          }
          placeholder="Enter your name"
          value={formData.userName}
          onChange={(e) => updateForm("userName", e.currentTarget.value)}
        />

        {/* Designation */}
        <TextInput
          label={
            <Text fz="sm" fw={500}>
              Designation{" "}
              <Text span c="red" fz="lg">
                *
              </Text>
            </Text>
          }
          placeholder="Enter your designation"
          value={formData.userDesignation}
          onChange={(e) => updateForm("userDesignation", e.currentTarget.value)}
        />

        {/* Phone Number */}
        <TextInput
          label={
            <Text fz="sm" fw={500}>
              Phone Number
            </Text>
          }
          placeholder="hidden"
          styles={{
            input: { display: "none" },
            wrapper: { display: "none" },
            root: { marginBottom: 0 },
          }}
          inputContainer={() => (
            <PhoneInput
              defaultCountry="in"
              value={`${formData.userPhoneCountryCode}${formData.userPhoneNumber}`}
              onChange={(phone, meta) => {
                const countryCode = meta.country?.dialCode ? `+${meta.country.dialCode}` : "";
                const nationalNumber = phone.replace(`+${meta.country?.dialCode || ""}`, "").trim();
                updateForm("userPhoneCountryCode", countryCode);
                updateForm("userPhoneNumber", nationalNumber);
              }}
              placeholder="123-456-7890"
              style={{ width: "100%" }}
              inputStyle={{
                width: "100%",
                height: "36px",
                fontSize: "14px",
                border: "1px solid var(--mantine-color-gray-3)",
                borderLeft: "none",
                borderRadius: "0 var(--mantine-radius-md) var(--mantine-radius-md) 0",
                outline: "none",
              }}
              countrySelectorStyleProps={{
                buttonStyle: {
                  height: "36px",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: "var(--mantine-radius-md) 0 0 var(--mantine-radius-md)",
                  padding: "0 12px",
                  backgroundColor: "white",
                },
              }}
            />
          )}
        />
      </SimpleGrid>

      {showDocs && (
        <>
          <Divider my="xl" />

          <Text fz="sm" fw={500} mb={4}>
            Verification Documents{" "}
            <Text span c="red" fz="lg">
              *
            </Text>
          </Text>
          <Text fz="xs" c="slate.6" mb="lg">
            Upload at least one of the following documents for verification.
          </Text>

          {/* Authority Letter */}
          {showAuthorityLetterSection && (
            <Box mb="sm">
              <Flex justify="space-between" align="center" mb="xs">
                <Flex align="center" gap="xs">
                  <IconFileText size={16} color="var(--mantine-color-slate-6)" />
                  <Text fz="sm" fw={500}>
                    Authority Letter
                  </Text>
                </Flex>
                <Badge size="xs" color="red" variant="light">
                  MANDATORY
                </Badge>
              </Flex>

              {(authorityLetterData!.id || authorityLetterData!.existingUrl) && !authorityLetterData!.file ? (
                // Existing doc from DB — Paper card
                <Paper withBorder p="md" radius="md" style={{ borderColor: "#E5E7EB" }}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap="sm">
                      <IconFileText size={28} color="#2563EB" />
                      <Box>
                        <Text fw={500} fz="sm">
                          {authorityLetterData!.name}
                        </Text>
                        {(authorityLetterData as any).createdAt && (
                          <Text fz="xs" c="slate.6" mt={2}>
                            Uploaded {formatDate((authorityLetterData as any).createdAt)}
                          </Text>
                        )}
                        {authorityLetterData!.existingUrl && (
                          <Anchor href={authorityLetterData!.existingUrl} target="_blank" fz="xs">
                            View Document
                          </Anchor>
                        )}
                      </Box>
                    </Flex>
                    {canEditMandatory && onRemoveAuthorityLetterDoc && (
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        onClick={onRemoveAuthorityLetterDoc}
                      >
                        Remove
                      </Button>
                    )}
                  </Flex>
                </Paper>
              ) : authorityLetterData!.file ? (
                // New file selected — Paper card
                <Paper withBorder p="md" radius="md" style={{ borderColor: "#E5E7EB" }}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap="sm">
                      <IconFileText size={28} color="#2563EB" />
                      <Text fw={500} fz="sm">
                        {authorityLetterData!.file.name}
                      </Text>
                    </Flex>
                    {canEditMandatory && onRemoveAuthorityLetterDoc && (
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        onClick={onRemoveAuthorityLetterDoc}
                      >
                        Remove
                      </Button>
                    )}
                  </Flex>
                </Paper>
              ) : canEditMandatory ? (
                // Empty — Paper card with Upload button at end
                <Paper withBorder p="md" radius="md" style={{ borderColor: "#E5E7EB" }}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap="sm">
                      <IconFileText size={28} color="var(--mantine-color-slate-4)" />
                      <Box>
                        <Text fw={500} fz="sm">No document uploaded</Text>
                        <Text fz="xs" c="slate.6">Upload a signed authority letter</Text>
                      </Box>
                    </Flex>
                    <FileButton onChange={handleAuthorityLetterUpload} accept=".pdf,.doc,.docx">
                      {(props) => (
                        <Button size="xs" variant="outline" leftSection={<IconUpload size={13} />} {...props}>
                          Upload
                        </Button>
                      )}
                    </FileButton>
                  </Flex>
                </Paper>
              ) : (
                <Text fz="sm" c="slate.5">
                  Not provided
                </Text>
              )}
            </Box>
          )}

          {/* OR Divider — only when both sections are shown */}
          {showOrDivider && (
            <Flex align="center" gap="sm" my="sm">
              <Divider style={{ flex: 1 }} />
              <Text fz="xs" c="slate.5" fw={500}>
                OR
              </Text>
              <Divider style={{ flex: 1 }} />
            </Flex>
          )}

          {/* Affidavit */}
          {showAffidavitSection && (
            <Box mt="sm">
              <Flex justify="space-between" align="center" mb="xs">
                <Flex align="center" gap="xs">
                  <IconFileText size={16} color="var(--mantine-color-slate-6)" />
                  <Text fz="sm" fw={500}>
                    Affidavit
                  </Text>
                </Flex>
                <Badge size="xs" color="red" variant="light">
                  MANDATORY
                </Badge>
              </Flex>

              {(mandatoryData!.id || mandatoryData!.existingUrl) && !mandatoryData!.file ? (
                // Existing doc from DB — Paper card
                <Paper withBorder p="md" radius="md" style={{ borderColor: "#E5E7EB" }}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap="sm">
                      <IconFileText size={28} color="#2563EB" />
                      <Box>
                        <Text fw={500} fz="sm">
                          {mandatoryData!.name}
                        </Text>
                        {(mandatoryData as any).createdAt && (
                          <Text fz="xs" c="slate.6" mt={2}>
                            Uploaded {formatDate((mandatoryData as any).createdAt)}
                          </Text>
                        )}
                        {mandatoryData!.existingUrl && (
                          <Anchor href={mandatoryData!.existingUrl} target="_blank" fz="xs">
                            View Document
                          </Anchor>
                        )}
                      </Box>
                    </Flex>
                    {canEditMandatory && onRemoveMandatoryDoc && (
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        onClick={onRemoveMandatoryDoc}
                      >
                        Remove
                      </Button>
                    )}
                  </Flex>
                </Paper>
              ) : mandatoryData!.file ? (
                // New file selected — Paper card
                <Paper withBorder p="md" radius="md" style={{ borderColor: "#E5E7EB" }}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap="sm">
                      <IconFileText size={28} color="#2563EB" />
                      <Text fw={500} fz="sm">
                        {mandatoryData!.file.name}
                      </Text>
                    </Flex>
                    {canEditMandatory && onRemoveMandatoryDoc && (
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        onClick={onRemoveMandatoryDoc}
                      >
                        Remove
                      </Button>
                    )}
                  </Flex>
                </Paper>
              ) : canEditMandatory ? (
                // Empty — Paper card with Upload button at end
                <Paper withBorder p="md" radius="md" style={{ borderColor: "#E5E7EB" }}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap="sm">
                      <IconFileText size={28} color="var(--mantine-color-slate-4)" />
                      <Box>
                        <Text fw={500} fz="sm">No document uploaded</Text>
                        <Text fz="xs" c="slate.6">Upload a signed affidavit</Text>
                      </Box>
                    </Flex>
                    <FileButton onChange={handleAffidavitUpload} accept=".pdf,.doc,.docx">
                      {(props) => (
                        <Button size="xs" variant="outline" leftSection={<IconUpload size={13} />} {...props}>
                          Upload
                        </Button>
                      )}
                    </FileButton>
                  </Flex>
                </Paper>
              ) : (
                <Text fz="sm" c="slate.5">
                  Not provided
                </Text>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
